import { Chip } from '@mui/material';
import dayjs from 'dayjs';
import { GridColDef } from '@mui/x-data-grid';
import { IAnalyticsColumnMeta } from '../../types/api/response/analyticsResponse';

/**
 * Column-metadata-driven rendering: the analytics catalog declares each column's key,
 * label and type, and this maps them to DataGrid columns with consistent formatting.
 * Every current AND future report renders through this — one styling decision, applied
 * everywhere (Justin's directive: consistent style, reusable components).
 */

const fjd = (currency?: string) =>
  new Intl.NumberFormat('en-FJ', { style: 'currency', currency: currency ?? 'FJD' });

export const formatCellValue = (
  value: string | number | boolean | null | undefined,
  meta: IAnalyticsColumnMeta,
): string => {
  if (value === null || value === undefined || value === '') return '—';
  switch (meta.type) {
    case 'money':
      return fjd(meta.currency).format(Number(value));
    case 'percent':
      return `${Number(value).toFixed(1)}%`;
    case 'number':
      return Number(value).toLocaleString('en-FJ');
    case 'date':
      return dayjs(String(value)).format('DD MMM YYYY');
    case 'datetime':
      return dayjs(String(value)).format('DD MMM YYYY, HH:mm');
    case 'boolean':
      return value === true || value === 1 || value === '1' ? 'Yes' : 'No';
    default:
      return String(value);
  }
};

export const toGridColumn = (meta: IAnalyticsColumnMeta): GridColDef => ({
  field: meta.key,
  headerName: meta.label,
  flex: 1,
  minWidth: 130,
  align: ['number', 'money', 'percent'].includes(meta.type) ? 'right' : 'left',
  headerAlign: ['number', 'money', 'percent'].includes(meta.type) ? 'right' : 'left',
  renderCell: (params) =>
    meta.type === 'enum' ? (
      <Chip size="small" label={formatCellValue(params.value, meta)} />
    ) : (
      formatCellValue(params.value, meta)
    ),
});
